/**
 * serverView — the `data.source: "server_view"` feed (README §4.7).
 *
 * A server view is a read-only row set the server keeps for the user
 * (`GET /api/widget-native/views/<view>`), rendered through the SAME
 * fold/selector lane as the user feed: each row becomes an entry
 * `{id, kind, created_at, payload: row}` and `meta` binds as `view_meta`.
 * Writes go through the view's server-side action allowlist only
 * (`host.viewAction`), never `logData`. Flutter twin: `native_widget_runtime.dart`.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../../services/api';
import { describeApiError } from '../../services/apiError';

const VIEWS_BASE = '/api/widget-native/views';

/** The blueprint's server view name, or null for a user-feed blueprint. */
export function serverViewOf(blueprint) {
  const data = blueprint?.data;
  if (!data || data.source !== 'server_view') return null;
  const view = typeof data.view === 'string' ? data.view.trim() : '';
  return view || null;
}

function rowToEntry(row) {
  const id = row?.id != null ? String(row.id) : null;
  return {
    id,
    kind: row?.kind ?? null,
    created_at: row?.created_at ?? row?.received_at ?? null,
    payload: { ...row, id },
  };
}

/** A view response `{rows, meta}` as the host feed shape the render context reads. */
export function serverViewFeed(body, viewName = null) {
  const rows = Array.isArray(body?.rows) ? body.rows.filter((r) => r && typeof r === 'object') : [];
  const meta = body?.meta && typeof body.meta === 'object' && !Array.isArray(body.meta) ? body.meta : {};
  return {
    widget_id: null,
    view: viewName,
    entries: rows.map(rowToEntry),
    settings: {},
    pending: { adds: [], updates: [] },
    view_meta: meta,
  };
}

/** `optimistic {ids, patch}` merged into the matching entries' payloads, in place of a round trip. */
export function applyOptimisticPatch(feed, optimistic) {
  const ids = Array.isArray(optimistic?.ids) ? optimistic.ids.map(String) : [];
  const patch = optimistic?.patch;
  if (!feed || !ids.length || !patch || typeof patch !== 'object') return feed;
  const want = new Set(ids);
  return {
    ...feed,
    entries: (feed.entries || []).map((e) => (want.has(String(e.id))
      ? { ...e, payload: { ...(e.payload || {}), ...patch } }
      : e)),
  };
}

/**
 * In-flight `host.viewAction`s, process-wide: every surface reading a view
 * shows their optimistic patch and re-reads once each settles. Flutter twin:
 * `ViewIrServerViewActions`.
 */
const inFlight = new Set();
const settledListeners = new Set();

function overlayInFlight(view, feed) {
  let out = feed;
  for (const a of inFlight) if (a.view === view) out = applyOptimisticPatch(out, a.optimistic);
  return out;
}

function settleAction(action) {
  if (!inFlight.delete(action)) return;
  for (const listener of [...settledListeners]) listener(action.view, action.origin);
}

async function readJson(resp, label) {
  if (resp.ok) return resp.json();
  let detail = null;
  try { detail = (await resp.json())?.detail ?? null; } catch { /* non-JSON error body */ }
  const err = new Error(`${label} failed: ${resp.status}`);
  err.status = resp.status;
  err.detail = detail;
  throw err;
}

export async function fetchServerView(view, { signal } = {}) {
  const resp = await api.authFetch(`${VIEWS_BASE}/${encodeURIComponent(view)}`, { signal });
  return readJson(resp, `view ${view}`);
}

export async function postServerViewAction(view, action, args = {}) {
  const resp = await api.authFetch(
    `${VIEWS_BASE}/${encodeURIComponent(view)}/actions/${encodeURIComponent(action)}`,
    { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(args || {}) },
  );
  return readJson(resp, `view action ${action}`);
}

const OFF = { state: 'off', feed: null, error: null };

/**
 * Live server-view feed. `reload()` re-reads in place (rows and component state
 * survive); `act(payload)` runs one `host.viewAction`: optimistic patch now,
 * POST, then a re-read that replaces the patched rows (or rolls them back).
 */
export function useServerViewFeed(enabled, view, reloadKey = 0) {
  const [live, setLive] = useState(OFF);
  const ctrl = useRef(null);
  const viewRef = useRef(view);
  viewRef.current = view;

  const reload = useCallback(async () => {
    const name = viewRef.current;
    if (!name) return;
    ctrl.current?.abort();
    const controller = new AbortController();
    ctrl.current = controller;
    setLive((prev) => (prev.feed && prev.view === name ? { ...prev, error: null } : { ...OFF, state: 'loading', view: name }));
    try {
      const body = await fetchServerView(name, { signal: controller.signal });
      if (controller.signal.aborted) return;
      setLive({ state: 'ready', view: name, error: null, feed: overlayInFlight(name, serverViewFeed(body, name)) });
    } catch (error) {
      if (controller.signal.aborted || error?.name === 'AbortError') return;
      // With nothing loaded yet the page still gets a feed: empty rows plus
      // `view_meta.error`, so a blueprint can draw its retry instead of a blank.
      setLive((prev) => (prev.feed?.view_meta?.available !== undefined && prev.view === name
        ? { ...prev, error: describeApiError(error) }
        : {
          ...OFF, view: name, state: 'error', error: describeApiError(error),
          feed: serverViewFeed({ rows: [], meta: { error: 'unavailable' } }, name),
        }));
    }
  }, []);

  useEffect(() => {
    if (!enabled || !view) { setLive(OFF); return undefined; }
    reload();
    return () => ctrl.current?.abort();
  }, [enabled, view, reloadKey, reload]);

  const self = useRef({});
  useEffect(() => {
    if (!enabled || !view) return undefined;
    const onSettled = (name, origin) => { if (name === view && origin !== self.current) reload(); };
    settledListeners.add(onSettled);
    return () => { settledListeners.delete(onSettled); };
  }, [enabled, view, reload]);

  const act = useCallback(async (payload = {}) => {
    const name = (typeof payload.view === 'string' && payload.view) || viewRef.current;
    const action = typeof payload.action === 'string' ? payload.action : '';
    if (!name || !action) return { ok: false, action, error: 'missing view or action' };
    const record = { view: name, optimistic: payload.optimistic, origin: self.current };
    inFlight.add(record);
    if (payload.optimistic) {
      setLive((prev) => (prev.feed ? { ...prev, feed: applyOptimisticPatch(prev.feed, payload.optimistic) } : prev));
    }
    let answer;
    try {
      const body = await postServerViewAction(name, action, payload.args || {});
      answer = { ok: true, action, result: body?.result ?? null };
    } catch (error) {
      answer = { ok: false, action, error: describeApiError(error) };
    } finally {
      settleAction(record);
    }
    await reload();
    return answer;
  }, [reload]);

  if (!enabled || !view) return { ...OFF, reload, act };
  return { ...(live.view === view ? live : { ...OFF, state: 'loading' }), reload, act };
}
